import React, { useEffect, useRef } from 'react';
import { getAccessToken } from '../api';
import './Admin.css';

// Ported from the old server/public/admin.html static panel. It's plain
// DOM-manipulation JS (not React) because the original page's inline
// onclick="..." handlers only resolve against window-scoped functions —
// dangerouslySetInnerHTML preserves that wiring exactly as it worked before,
// which was far lower-risk than re-deriving ~20 interdependent handlers
// (import, batch import, field toggling, schema verify diffs, pricing sync)
// as React state. Auth is the only real change: the old X-Admin-Session
// header is gone, replaced by the Supabase bearer token.
const PANEL_HTML = `
  <div class="tabs" style="margin-bottom: 2rem;">
    <button class="tab active" onclick="switchMainTab('import-models')">📥 Import Models</button>
    <button class="tab" onclick="switchMainTab('model-management')">⚙️ Model Management</button>
  </div>

  <div id="import-models" class="tab-content active">
    <div class="section">
      <div class="section-header">Import Models</div>
      <div class="section-content">
        <div id="import-status" class="alert" style="display: none; margin-bottom: 1rem;"></div>

        <div class="tabs">
          <button class="tab active" onclick="switchTab('single-import')">Single Import</button>
          <button class="tab" onclick="switchTab('batch-import')">Batch Import</button>
        </div>

        <div id="single-import" class="tab-content active">
          <div class="form-group">
            <label for="endpoint-id">Endpoint ID (Recommended):</label>
            <input type="text" id="endpoint-id" placeholder="fal-ai/flux/dev">
          </div>
          <div class="form-group">
            <label for="schema-url">Or Full Schema URL:</label>
            <input type="text" id="schema-url" placeholder="https://fal.ai/api/openapi/queue/openapi.json?endpoint_id=...">
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" id="dry-run"> Dry Run (test without changes)
            </label>
          </div>
          <button onclick="importModel()">Import Model</button>
          <button class="secondary" onclick="clearImportForm()">Clear</button>
        </div>

        <div id="batch-import" class="tab-content">
          <div class="form-group">
            <label for="endpoint-list">Endpoint IDs (one per line):</label>
            <textarea id="endpoint-list" rows="5" placeholder="fal-ai/flux/dev&#10;fal-ai/reve/edit&#10;fal-ai/stable-diffusion-v3-medium"></textarea>
          </div>
          <div class="form-group">
            <label class="checkbox-label">
              <input type="checkbox" id="batch-dry-run"> Dry Run (test without changes)
            </label>
          </div>
          <button onclick="batchImport()">Batch Import</button>
          <button class="secondary" onclick="clearBatchForm()">Clear</button>
        </div>
      </div>
    </div>
  </div>

  <div id="model-management" class="tab-content">
    <div class="section">
      <div class="section-header">Model Management</div>
      <div class="section-content">
        <div style="display: flex; gap: 0.5rem; flex-wrap: wrap; margin-bottom: 1.5rem;">
          <button onclick="loadModels()">Refresh Models</button>
          <button class="secondary" onclick="togglePricingPanel()">💰 Sync Pricing</button>
        </div>

        <div id="pricing-panel" style="display: none; margin-bottom: 2rem; border: 1px solid #e9ecef; border-radius: 8px; overflow: hidden;">
          <div style="background: #f8f9fa; padding: 0.75rem 1rem; border-bottom: 1px solid #e9ecef; display: flex; justify-content: space-between; align-items: center;">
            <strong>Pricing from fal.ai</strong>
            <button id="fetch-pricing-btn" onclick="fetchPricing()" style="margin: 0;">Fetch Pricing</button>
          </div>
          <div id="pricing-container" style="padding: 1rem;">
            <p style="color: #6c757d; margin: 0;">Click "Fetch Pricing" to retrieve current pricing from the fal.ai API and compare with your database.</p>
          </div>
        </div>

        <div id="models-container"></div>
      </div>
    </div>
  </div>

  <div id="verify-modal" class="modal-overlay" style="display: none;" onclick="if(event.target===this)closeVerifyModal()">
    <div class="modal">
      <div class="modal-header">
        <h3 id="verify-modal-title">Verify Model</h3>
        <button class="close-btn" onclick="closeVerifyModal()">×</button>
      </div>
      <div id="verify-modal-content"></div>
      <div id="verify-modal-footer" style="margin-top: 1.5rem; display: flex; gap: 0.5rem; justify-content: flex-end; border-top: 1px solid #e9ecef; padding-top: 1rem;">
        <button class="secondary" onclick="closeVerifyModal()">Close</button>
      </div>
    </div>
  </div>

  <div id="messages"></div>
`;

const WINDOW_KEYS = [
  'apiCall', 'importModel', 'batchImport', 'loadModels', 'renderModels',
  'showModelDetails', 'renderModelDetails', 'toggleField', 'switchTab',
  'switchMainTab', 'clearImportForm', 'clearBatchForm', 'showImportStatus',
  'hideImportStatus', 'showMessage', 'verifyModel', 'openVerifyModal',
  'closeVerifyModal', 'reimportFromVerify', 'togglePricingPanel',
  'fetchPricing', 'renderPricingTable', 'applyAllPricing', 'applySinglePricing',
  '_verifyEndpointId', '_pricingData',
];

function mountAdminScript() {
  let models = [];
  const apiBase = `${process.env.REACT_APP_API_URL}/api/admin`;

  async function apiCall(endpoint, method = 'GET', data = null) {
    const token = await getAccessToken();
    const options = {
      method,
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
    };
    if (data) options.body = JSON.stringify(data);

    const response = await fetch(`${apiBase}${endpoint}`, options);
    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'API request failed');
    }
    return await response.json();
  }

  async function importModel() {
    const endpointId = document.getElementById('endpoint-id').value.trim();
    const schemaUrl = document.getElementById('schema-url').value.trim();
    const dryRun = document.getElementById('dry-run').checked;

    if (!endpointId && !schemaUrl) {
      showImportStatus('Please provide either an endpoint ID or schema URL', 'error');
      return;
    }

    try {
      showImportStatus('Importing model...', 'warning');
      const result = await apiCall('/import', 'POST', {
        endpointId: endpointId || undefined,
        url: schemaUrl || undefined,
        dryRun,
      });
      showImportStatus(result.message, 'success');
      if (!dryRun) loadModels();
    } catch (error) {
      showImportStatus('Import failed: ' + error.message, 'error');
    }
  }

  async function batchImport() {
    const endpointList = document.getElementById('endpoint-list').value.trim();
    const dryRun = document.getElementById('batch-dry-run').checked;

    if (!endpointList) {
      showMessage('Please provide endpoint IDs', 'error');
      return;
    }

    const endpointIds = endpointList.split('\n').map((line) => line.trim()).filter(Boolean);

    try {
      showImportStatus(`Batch importing ${endpointIds.length} models...`, 'warning');
      const result = await apiCall('/batch-import', 'POST', { endpointIds, dryRun });
      showImportStatus(`${result.message} (${result.summary.successful} successful, ${result.summary.failed} failed)`, 'success');
      if (!dryRun) loadModels();
    } catch (error) {
      showImportStatus('Batch import failed: ' + error.message, 'error');
    }
  }

  async function loadModels() {
    try {
      models = await apiCall('/models');
      renderModels();
    } catch (error) {
      showMessage('Failed to load models: ' + error.message, 'error');
    }
  }

  function renderModels() {
    const container = document.getElementById('models-container');
    if (models.length === 0) {
      container.innerHTML = '<p>No models found. Import some models to get started!</p>';
      return;
    }

    container.innerHTML = `
      <h3>Models (${models.length})</h3>
      <div class="models-grid">
        ${models.map((model) => `
          <div class="model-card" id="model-card-${model.id}">
            <div class="model-header">
              <div class="model-title">${model.display_name || model.name}</div>
              <div class="model-category">${model.category || 'uncategorized'}</div>
            </div>
            <div style="font-size: 0.9rem; color: #6c757d; margin-bottom: 0.5rem;">
              ${model.api_endpoint}
            </div>
            ${model.description ? `<div style="font-size: 0.9rem; margin-bottom: 0.5rem;">${model.description.substring(0, 100)}${model.description.length > 100 ? '…' : ''}</div>` : ''}
            <div class="field-stats">
              <div class="stat visible">👁️ ${model.fieldStats.visible} visible</div>
              <div class="stat hidden">🙈 ${model.fieldStats.hidden} hidden</div>
            </div>
            ${model.cost ? `<div class="model-cost">💰 ${model.cost}</div>` : ''}
            <div style="margin-top: 1rem;">
              <button onclick="showModelDetails(${model.id})" class="secondary">Manage Fields</button>
              <button onclick="verifyModel(${model.id})" class="secondary">Verify</button>
              ${model.playground_url ? `<button onclick="window.open('${model.playground_url}', '_blank')" class="secondary">Playground</button>` : ''}
            </div>
          </div>
        `).join('')}
      </div>
    `;
  }

  async function showModelDetails(modelId) {
    try {
      const model = await apiCall(`/models/${modelId}`);
      renderModelDetails(model);
    } catch (error) {
      showMessage('Failed to load model details: ' + error.message, 'error');
    }
  }

  function renderModelDetails(model) {
    const container = document.getElementById('models-container');
    container.innerHTML = `
      <div style="margin-bottom: 1rem;">
        <button onclick="loadModels()" class="secondary">← Back to Models</button>
      </div>
      <h3>${model.display_name || model.name}</h3>
      <p style="color: #6c757d; margin-bottom: 1rem;">${model.api_endpoint}</p>
      <div style="display: grid; grid-template-columns: 1fr 1fr; gap: 2rem;">
        <div>
          <h4>👁️ Visible Fields (${model.fields.visible.length})</h4>
          <div class="field-list">
            ${model.fields.visible.map((field) => `
              <div class="field-item">
                <div class="field-info">
                  <div class="field-name">${field.title}</div>
                  <div class="field-type">${field.type} (${field.fieldType})${field.required ? ' *' : ''}</div>
                </div>
                <button class="field-toggle danger" onclick="toggleField(${model.id}, '${field.name}', false)">Hide</button>
              </div>
            `).join('')}
          </div>
        </div>
        <div>
          <h4>🙈 Hidden Fields (${model.fields.hidden.length})</h4>
          <div class="field-list">
            ${model.fields.hidden.map((field) => `
              <div class="field-item">
                <div class="field-info">
                  <div class="field-name">${field.title}</div>
                  <div class="field-type">${field.type} (${field.fieldType})${field.required ? ' *' : ''}</div>
                  ${field.default !== undefined ? `<div style="font-size: 0.8rem; color: #28a745;">Default: ${field.default}</div>` : ''}
                </div>
                <button class="field-toggle success" onclick="toggleField(${model.id}, '${field.name}', true)">Show</button>
              </div>
            `).join('')}
          </div>
        </div>
      </div>
    `;
  }

  async function toggleField(modelId, fieldName, makeVisible) {
    try {
      const result = await apiCall(`/models/${modelId}/fields/${fieldName}/toggle`, 'POST', { makeVisible });
      showMessage(result.message, 'success');
      showModelDetails(modelId);
    } catch (error) {
      showMessage('Failed to toggle field: ' + error.message, 'error');
    }
  }

  function switchTab(tabName) {
    const importTabs = document.querySelectorAll('#import-models .tabs .tab');
    const importContent = document.querySelectorAll('#import-models .tab-content');
    importTabs.forEach((tab) => tab.classList.remove('active'));
    importContent.forEach((content) => content.classList.remove('active'));
    window.event.target.classList.add('active');
    document.getElementById(tabName).classList.add('active');
  }

  function switchMainTab(tabName, clickedEl) {
    const mainTabs = document.querySelectorAll('#admin-root > .tabs .tab');
    const mainContent = document.querySelectorAll('#admin-root > .tab-content');
    mainTabs.forEach((tab) => tab.classList.remove('active'));
    mainContent.forEach((content) => content.classList.remove('active'));
    const target = clickedEl || window.event.target;
    if (target && target.classList) target.classList.add('active');
    document.getElementById(tabName).classList.add('active');
  }

  function clearImportForm() {
    document.getElementById('endpoint-id').value = '';
    document.getElementById('schema-url').value = '';
    document.getElementById('dry-run').checked = false;
  }

  function clearBatchForm() {
    document.getElementById('endpoint-list').value = '';
    document.getElementById('batch-dry-run').checked = false;
  }

  function showImportStatus(message, type = 'info') {
    const statusDiv = document.getElementById('import-status');
    statusDiv.className = `alert alert-${type}`;
    statusDiv.textContent = message;
    statusDiv.style.display = 'block';
    if (type === 'success') {
      setTimeout(() => { statusDiv.style.display = 'none'; }, 5000);
    }
  }

  function hideImportStatus() {
    document.getElementById('import-status').style.display = 'none';
  }

  function showMessage(message, type) {
    const messagesDiv = document.getElementById('messages');
    const messageDiv = document.createElement('div');
    messageDiv.className = `alert alert-${type}`;
    messageDiv.textContent = message;
    messagesDiv.appendChild(messageDiv);
    setTimeout(() => { messageDiv.remove(); }, 5000);
  }

  async function verifyModel(modelId) {
    const model = models.find((m) => m.id === modelId);
    const modelName = model ? (model.display_name || model.name) : `Model ${modelId}`;
    openVerifyModal(modelName, null, true);
    try {
      const result = await apiCall(`/models/${modelId}/verify`);
      openVerifyModal(modelName, result, false);
    } catch (error) {
      openVerifyModal(modelName, null, false, error.message);
    }
  }

  function openVerifyModal(modelName, result, loading, errorMsg) {
    const modal = document.getElementById('verify-modal');
    const footer = document.getElementById('verify-modal-footer');
    modal.style.display = 'flex';
    document.getElementById('verify-modal-title').textContent = `Verify: ${modelName}`;
    const content = document.getElementById('verify-modal-content');

    if (loading) {
      content.innerHTML = '<div style="text-align:center;padding:2rem"><div class="loading"></div><p style="margin-top:1rem;color:#6c757d">Fetching live schema from fal.ai…</p></div>';
      footer.innerHTML = '<button class="secondary" onclick="closeVerifyModal()">Close</button>';
      return;
    }

    if (errorMsg) {
      content.innerHTML = `<div class="alert alert-error"><strong>Error:</strong> ${errorMsg}</div>`;
      footer.innerHTML = '<button class="secondary" onclick="closeVerifyModal()">Close</button>';
      return;
    }

    const { status, schemaChanges, metadataChanges, totalChanges, dbVersion, liveVersion, apiEndpoint } = result;

    if (status === 'up_to_date') {
      content.innerHTML = `
        <div class="alert alert-success">
          <strong>✓ Up to date</strong> — no changes detected between database and live fal.ai API.
          ${dbVersion ? `<br><small>Version: ${dbVersion}</small>` : ''}
        </div>`;
      footer.innerHTML = '<button class="secondary" onclick="closeVerifyModal()">Close</button>';
      return;
    }

    let html = `<div class="alert alert-warning">
      <strong>${totalChanges} change${totalChanges === 1 ? '' : 's'} detected</strong>
      ${(dbVersion || liveVersion) ? `<br><small>DB: ${dbVersion || 'n/a'} &rarr; Live: ${liveVersion || 'n/a'}</small>` : ''}
    </div>`;

    if (Object.keys(metadataChanges).length > 0) {
      html += '<div class="diff-section"><h4>Metadata Changes</h4>';
      for (const [field, { db, live }] of Object.entries(metadataChanges)) {
        html += `<div class="diff-changed">
          <strong>${field}</strong>
          <div class="diff-change-detail">
            <span class="diff-old">${db ?? 'null'}</span>
            &rarr;
            <span class="diff-new">${live ?? 'null'}</span>
          </div>
        </div>`;
      }
      html += '</div>';
    }

    if (schemaChanges.added.length > 0) {
      html += `<div class="diff-section"><h4>New Fields (${schemaChanges.added.length})</h4>`;
      for (const { field, liveDefinition } of schemaChanges.added) {
        html += `<div class="diff-added">
          <strong>${liveDefinition.title || field}</strong> <code>${field}</code>
          <div class="diff-change-detail">${liveDefinition.type || ''}${liveDefinition.required ? ' · required' : ''}${liveDefinition.description ? ` · ${liveDefinition.description.substring(0, 80)}` : ''}</div>
        </div>`;
      }
      html += '</div>';
    }

    if (schemaChanges.removed.length > 0) {
      html += `<div class="diff-section"><h4>Removed Fields (${schemaChanges.removed.length})</h4>`;
      for (const { field, dbDefinition } of schemaChanges.removed) {
        html += `<div class="diff-removed">
          <strong>${dbDefinition.title || field}</strong> <code>${field}</code>
          <div class="diff-change-detail">${dbDefinition.type || ''}${dbDefinition.required ? ' · required' : ''}</div>
        </div>`;
      }
      html += '</div>';
    }

    if (schemaChanges.changed.length > 0) {
      html += `<div class="diff-section"><h4>Changed Fields (${schemaChanges.changed.length})</h4>`;
      for (const { field, changes } of schemaChanges.changed) {
        html += `<div class="diff-changed"><strong>${field}</strong>`;
        for (const [key, { db, live }] of Object.entries(changes)) {
          html += `<div class="diff-change-detail">
            <em>${key}:</em>
            <span class="diff-old">${JSON.stringify(db)}</span>
            &rarr;
            <span class="diff-new">${JSON.stringify(live)}</span>
          </div>`;
        }
        html += '</div>';
      }
      html += '</div>';
    }

    content.innerHTML = html;
    window._verifyEndpointId = apiEndpoint;
    footer.innerHTML = `
      <button class="secondary" onclick="closeVerifyModal()">Close</button>
      <button class="warning" onclick="reimportFromVerify()">Re-import from Live API</button>
    `;
  }

  function closeVerifyModal() {
    document.getElementById('verify-modal').style.display = 'none';
  }

  async function reimportFromVerify() {
    const endpointId = window._verifyEndpointId;
    if (!endpointId) return;
    closeVerifyModal();
    const importTab = document.querySelector('#admin-root > .tabs .tab');
    switchMainTab('import-models', importTab);
    showImportStatus(`Re-importing ${endpointId}…`, 'warning');
    try {
      const result = await apiCall('/import', 'POST', { endpointId, dryRun: false });
      showImportStatus(result.message, 'success');
      loadModels();
    } catch (error) {
      showImportStatus('Re-import failed: ' + error.message, 'error');
    }
  }

  function togglePricingPanel() {
    const panel = document.getElementById('pricing-panel');
    panel.style.display = panel.style.display === 'none' ? 'block' : 'none';
  }

  async function fetchPricing() {
    const btn = document.getElementById('fetch-pricing-btn');
    const container = document.getElementById('pricing-container');
    btn.disabled = true;
    btn.textContent = 'Fetching…';
    container.innerHTML = '<div style="text-align:center;padding:1rem"><div class="loading"></div><p style="margin-top:0.5rem;color:#6c757d">Fetching pricing from fal.ai…</p></div>';

    try {
      const result = await apiCall('/pricing');
      renderPricingTable(result);
    } catch (error) {
      container.innerHTML = `<div class="alert alert-error">Error fetching pricing: ${error.message}</div>`;
    } finally {
      btn.disabled = false;
      btn.textContent = 'Fetch Pricing';
    }
  }

  function renderPricingTable(data) {
    const container = document.getElementById('pricing-container');
    const { pricing, errors } = data;

    if (!pricing || pricing.length === 0) {
      container.innerHTML = '<div class="alert alert-warning">No pricing data returned from fal.ai.</div>';
      return;
    }

    const updates = pricing.filter((p) => p.pricingAvailable && p.formattedPrice !== p.currentCost);
    window._pricingData = pricing;

    let html = '';
    if (errors && errors.length > 0) {
      html += `<div class="alert alert-warning" style="margin-bottom:1rem">Warning: ${errors.join('; ')}</div>`;
    }

    if (updates.length > 0) {
      html += `<div style="margin-bottom:1rem;display:flex;align-items:center;gap:1rem;">
        <span><strong>${updates.length}</strong> model${updates.length === 1 ? '' : 's'} have pricing updates available</span>
        <button class="success" onclick="applyAllPricing()" style="margin:0">Apply All Updates</button>
      </div>`;
    } else if (pricing.some((p) => p.pricingAvailable)) {
      html += '<div class="alert alert-success" style="margin-bottom:1rem">All model pricing is already up to date.</div>';
    }

    html += `<table class="pricing-table">
      <thead><tr>
        <th>Model</th>
        <th>Endpoint</th>
        <th>DB Cost</th>
        <th>fal.ai Price</th>
        <th></th>
      </tr></thead>
      <tbody>
        ${pricing.map((p) => `<tr>
          <td>${p.modelName}</td>
          <td style="font-size:0.8rem;color:#6c757d">${p.apiEndpoint}</td>
          <td>${p.currentCost || '<em style="color:#6c757d">TBD</em>'}</td>
          <td class="${p.pricingAvailable ? (p.formattedPrice !== p.currentCost ? 'pricing-update' : 'pricing-match') : 'pricing-unavailable'}">
            ${p.pricingAvailable ? p.formattedPrice : 'Not available'}
          </td>
          <td>
            ${p.pricingAvailable && p.formattedPrice !== p.currentCost
              ? `<button class="success" style="padding:0.25rem 0.75rem;font-size:0.85rem;margin:0" onclick="applySinglePricing(${p.modelId}, this)">Apply</button>`
              : ''}
          </td>
        </tr>`).join('')}
      </tbody>
    </table>`;

    container.innerHTML = html;
  }

  async function applyAllPricing() {
    if (!window._pricingData) return;
    const updates = window._pricingData
      .filter((p) => p.pricingAvailable && p.formattedPrice !== p.currentCost)
      .map((p) => ({ modelId: p.modelId, cost: p.formattedPrice }));

    if (updates.length === 0) return;

    try {
      const result = await apiCall('/pricing/apply', 'POST', { updates });
      showMessage(`Updated pricing for ${result.updated} model${result.updated === 1 ? '' : 's'}`, 'success');
      fetchPricing();
      loadModels();
    } catch (error) {
      showMessage('Failed to apply pricing: ' + error.message, 'error');
    }
  }

  async function applySinglePricing(modelId, btn) {
    const entry = window._pricingData && window._pricingData.find((p) => p.modelId === modelId);
    const cost = entry && entry.formattedPrice;
    if (!cost) return;
    btn.disabled = true;
    btn.textContent = '…';
    try {
      await apiCall('/pricing/apply', 'POST', { updates: [{ modelId, cost }] });
      btn.textContent = 'Applied!';
      btn.className = 'secondary';
      btn.style = 'padding:0.25rem 0.75rem;font-size:0.85rem;margin:0';
      showMessage('Pricing updated', 'success');
      loadModels();
      if (window._pricingData) {
        const updated = window._pricingData.find((p) => p.modelId === modelId);
        if (updated) updated.currentCost = cost;
      }
    } catch (error) {
      btn.disabled = false;
      btn.textContent = 'Apply';
      showMessage('Failed: ' + error.message, 'error');
    }
  }

  Object.assign(window, {
    apiCall, importModel, batchImport, loadModels, renderModels,
    showModelDetails, renderModelDetails, toggleField, switchTab,
    switchMainTab, clearImportForm, clearBatchForm, showImportStatus,
    hideImportStatus, showMessage, verifyModel, openVerifyModal,
    closeVerifyModal, reimportFromVerify, togglePricingPanel,
    fetchPricing, renderPricingTable, applyAllPricing, applySinglePricing,
  });

  loadModels();
}

const Admin = () => {
  const containerRef = useRef(null);

  useEffect(() => {
    mountAdminScript();
    return () => {
      WINDOW_KEYS.forEach((key) => { delete window[key]; });
    };
  }, []);

  return (
    <div id="admin-root" ref={containerRef} dangerouslySetInnerHTML={{ __html: PANEL_HTML }} />
  );
};

export default Admin;
